import { Injectable } from '@angular/core';
import { HttpClient } from '@angular/common/http';
import { Observable, throwError } from 'rxjs';
import { map, catchError } from 'rxjs/operators';
import {
  ARBleCampaign,
  ARBleCampaignConfiguration,
  ARBleOrder,
  ARBleOrderItem,
  ARBleParcel,
} from '../models/agrorendement-ble.model';
import { Page } from '../models/page.model';
import { OrderItemDeletionSummary, normalizeDeletionSummary } from '../models/order-item-deletion.model';
import { Parcel } from '../models/parcel.model';
import { User } from '../models/user.model';
import { HttpWanaka } from '../utils/http.wanaka';
import { currentDateFormatted, tryJson } from '../utils/utils';
import {
  ARCampaignNotificationFilter,
  AROrderFilter,
  ARResultService,
  OrderSummary,
  fillOrderFilter,
} from './agrorendement-common.service';

import * as fileSaver from 'file-saver';
export class ARBleParcelFilter {
  prefixParcel: string;
  prefixExploitation: string;
  farmId: number | string;
  campaignId: number | number[];
  organisationId: number | string;
  orderStatus: boolean;
  completionStatus: boolean;
  prefixIdParcel: number;
  specy: number | string;
  advisorId: number | string;
}

@Injectable({ providedIn: 'root' })
export class ARBleService extends ARResultService<ARBleOrderItem, ARBleCampaign> {
  // utilisé par ARResultService
  protected toolUrl = '/tools/agrorendement/ble';

  constructor(
    protected http: HttpClient,
    protected httpWanaka: HttpWanaka,
  ) {
    super(http, httpWanaka);
  }

  updateParcelsBatch(parcelIds: number[], newDate: any, passage: string, isSuspended: any): Observable<any> {
    const payload: any = { parcelIds, passage };

    if (newDate) {
      payload.newDate = newDate;
    }

    if (isSuspended !== undefined && isSuspended !== null) {
      payload.isSuspended = isSuspended;
    }

    return this.http.put<any>(`${this.toolUrl}/orderitems/update-batch`, payload);
  }

  getPageCampaigns(
    pageIndex: number,
    itemsPerPage: number,
    sortColumnName: string,
    sortColumnOrder: string,
    filter: string,
  ): Observable<Page<ARBleCampaign>> {
    let url = this.toolUrl + '/campaigns?size=' + itemsPerPage + '&page=' + (pageIndex - 1);
    // filter pas supporté pour l'instant
    if (filter) {
      url += '&year=' + filter;
    }
    if (sortColumnName) {
      url += '&sort=' + sortColumnName + ',' + sortColumnOrder;
    }
    return this.http.get<any>(url).pipe(
      map((response) => {
        const data = response.data;
        data.content.forEach((element: ARBleCampaign) => {
          this.handleCampaignDates(element);
          if (element.configuration) {
            this.handleConfigurationDates(element.configuration);
          }
        });
        return data;
      }),
      catchError(this.handleError),
    );
  }

  getCampaigns(): Observable<ARBleCampaign[]> {
    return this.http.get<any>(this.toolUrl + '/campaigns/all?configurationStatus=true').pipe(
      map((response) => {
        const data: ARBleCampaign[] = response.data;
        data.forEach((element) => {
          this.handleCampaignDates(element);
          if (element.configuration) {
            this.handleConfigurationDates(element.configuration);
          }
        });
        return data;
      }),
      catchError(this.handleError),
    );
  }

  getCampaign(id: number): Observable<ARBleCampaign> {
    return this.http.get<any>(this.toolUrl + '/campaigns/' + id).pipe(
      map((response) => {
        const data: ARBleCampaign = response.data;
        this.handleCampaignDates(data);
        if (data.configuration) {
          this.handleConfigurationDates(data.configuration);
        }
        return data;
      }),
      catchError(this.handleError),
    );
  }

  getCampaignConfigurations(): Observable<ARBleCampaignConfiguration[]> {
    return this.http.get<any>(this.toolUrl + '/configs/all').pipe(
      map((response) => {
        const data: ARBleCampaignConfiguration[] = response.data;
        data.forEach((element: ARBleCampaignConfiguration) => {
          this.handleConfigurationDates(element);
          if (element.campaign) {
            this.handleCampaignDates(element.campaign);
          }
        });
        return data;
      }),
      catchError(this.handleError),
    );
  }

  getCampaignConfiguration(id: number): Observable<ARBleCampaignConfiguration> {
    return this.http.get<any>(this.toolUrl + '/configs/' + id).pipe(
      map((response) => {
        const data: ARBleCampaignConfiguration = this.handleConfigurationDates(response.data);
        if (data.campaign) {
          this.handleCampaignDates(data.campaign);
        }
        return data;
      }),
      catchError(this.handleError),
    );
  }

  saveCampaignConfiguration(config: ARBleCampaignConfiguration): Observable<ARBleCampaignConfiguration> {
    if (config.id) {
      return this.putCampaignConfiguration(config);
    }
    return this.postCampaignConfiguration(config);
  }

  deleteCampaignConfiguration(config: ARBleCampaignConfiguration) {
    const url = `${this.toolUrl}/configs/${config.id}`;

    return this.http.delete(url).pipe(catchError(this.handleError));
  }

  protected handleError(error: any) {
    // FIXME
    console.log('error');
    console.error('An error occurred', error);
    return throwError(() => error.message || error);
  }

  // Add new campaign
  private postCampaignConfiguration(config: ARBleCampaignConfiguration): Observable<ARBleCampaignConfiguration> {
    return this.http.post<any>(this.toolUrl + '/configs', config).pipe(
      map((res) => res.data),
      catchError(this.handleError),
    );
  }

  private putCampaignConfiguration(config: ARBleCampaignConfiguration) {
    const url = `${this.toolUrl}/configs/${config.id}`;

    return this.http.put<any>(url, config).pipe(
      map((res) => res.data),
      catchError(this.handleError),
    );
  }

  private handleCampaignDates(campaign: ARBleCampaign): ARBleCampaign {
    campaign.startingOn = new Date(<string>campaign.startingOn);
    campaign.endingOn = new Date(<string>campaign.endingOn);
    campaign.createdAt = new Date(<string>campaign.createdAt);
    campaign.updatedAt = new Date(<string>campaign.updatedAt);

    return campaign;
  }

  private handleConfigurationDates(config: ARBleCampaignConfiguration): ARBleCampaignConfiguration {
    if (config.startingOn) {
      config.startingOn = new Date(<string>config.startingOn);
    }
    if (config.orderT1EndingOn) {
      config.orderT1EndingOn = new Date(<string>config.orderT1EndingOn);
    }
    if (config.dataT1EndingOn) {
      config.dataT1EndingOn = new Date(<string>config.dataT1EndingOn);
    }
    if (config.orderT2EndingOn) {
      config.orderT2EndingOn = new Date(<string>config.orderT2EndingOn);
    }
    if (config.dataT2EndingOn) {
      config.dataT2EndingOn = new Date(<string>config.dataT2EndingOn);
    }
    if (config.dataT3StartingOn) {
      config.dataT3StartingOn = new Date(<string>config.dataT3StartingOn);
    }
    if (config.orderT3EndingOn) {
      config.orderT3EndingOn = new Date(<string>config.orderT3EndingOn);
    }
    if (config.dataT3EndingOn) {
      config.dataT3EndingOn = new Date(<string>config.dataT3EndingOn);
    }

    config.createdAt = new Date(<string>config.createdAt);
    config.updatedAt = new Date(<string>config.updatedAt);

    return config;
  }

  private handleOrderDates(order: ARBleOrder): ARBleOrder {
    order.createdAt = new Date(<string>order.createdAt);
    order.updatedAt = new Date(<string>order.updatedAt);

    return order;
  }

  getPageParcels(
    pageIndex: number,
    itemsPerPage: number,
    sortColumnName: string,
    sortColumnOrder: string,
    filter: ARBleParcelFilter,
  ): Observable<Page<ARBleParcel>> {
    let url = this.toolUrl + '/parcels?size=' + itemsPerPage + '&page=' + (pageIndex - 1);
    // filter pas supporté pour l'instant
    url += this.buildParamsFromFilter(filter);
    if (sortColumnName) {
      url += '&sort=' + sortColumnName + ',' + sortColumnOrder;
    }
    return this.http.get<any>(url).pipe(
      map((response) => {
        const data = response.data;
        data.content.forEach((element: ARBleParcel) => {
          this.handleParcel(element);
          if (element.campaign) {
            this.handleCampaignDates(<ARBleCampaign>element.campaign);
          }
          if ((<ARBleCampaign>element.campaign).configuration) {
            this.handleConfigurationDates((<ARBleCampaign>element.campaign).configuration);
          }
        });
        return data;
      }),
      catchError(this.handleError),
    );
  }

  getAllParcelIds(filter: ARBleParcelFilter): Observable<number[]> {
    let url = this.toolUrl + '/parcels/all?onlyIds=true';
    url += this.buildParamsFromFilter(filter);
    return this.http.get<any>(url).pipe(
      map((response) => {
        const data = response.data;
        return data.map((x: ARBleParcel) => x.id);
      }),
      catchError(this.handleError),
    );
  }

  buildParamsFromFilter(filter: ARBleParcelFilter): string {
    let url = '';
    if (filter) {
      if (filter.prefixParcel) {
        url += '&prefixParcel=' + filter.prefixParcel;
      }
      if (filter.prefixExploitation) {
        url += '&prefixExploitation=' + filter.prefixExploitation;
      }
      if (filter.prefixIdParcel) {
        url += '&prefixIdParcel=' + filter.prefixIdParcel;
      }
      if (filter.farmId) {
        url += '&farm=' + filter.farmId;
      }
      if (filter.organisationId) {
        url += '&organisation=' + filter.organisationId;
      }
      if (filter.campaignId) {
        if (typeof filter.campaignId === 'number' || typeof filter.campaignId === 'string') {
          url += '&campaign=' + filter.campaignId;
        } else {
          url += '&campaign=' + (<number[]>filter.campaignId).join(',');
        }
      }
      if (filter.orderStatus === true || filter.orderStatus === false) {
        url += '&orderStatus=' + filter.orderStatus;
      }
      if (filter.completionStatus === true || filter.completionStatus === false) {
        url += '&completionStatus=' + filter.completionStatus;
      }
      if (filter.specy) {
        url += '&specy=' + filter.specy;
      }
      if (filter.advisorId) {
        url += '&advisor=' + filter.advisorId;
      }
    }

    return url;
  }

  handleParcel(parcel: ARBleParcel): ARBleParcel {
    if (parcel.sowedOn) {
      parcel.sowedOn = new Date(<string>parcel.sowedOn);
    }
    parcel.createdAt = new Date(<string>parcel.createdAt);
    parcel.updatedAt = new Date(<string>parcel.updatedAt);
    return parcel;
  }

  getParcel(id: number): Observable<ARBleParcel> {
    return this.http.get<any>(this.toolUrl + '/parcels/' + id).pipe(
      map((response) => {
        const data: ARBleParcel = response.data;
        this.handleParcel(data);
        if (data.campaign) {
          this.handleCampaignDates(<ARBleCampaign>data.campaign);
        }
        if ((<ARBleCampaign>data.campaign).configuration) {
          this.handleConfigurationDates((<ARBleCampaign>data.campaign).configuration);
        }
        return data;
      }),
      catchError(this.handleError),
    );
  }

  getOrderItems(
    orderItemIds: Array<number>,
    completionStatus: boolean = null,
    orderStatus: boolean = null,
    orderableStatus: boolean = null,
  ): Observable<ARBleOrderItem[]> {
    let url = this.toolUrl + '/orderitems/all';
    url += this.fillOrderItemFilter(orderItemIds, completionStatus, orderStatus, orderableStatus);

    return this.http.get<any>(url).pipe(
      map((response) => {
        const data: ARBleOrderItem[] = response.data;
        data.forEach((element: ARBleOrderItem) => {
          this.handleOrderItemDates(element);
        });
        return data;
      }),
      catchError(this.handleError),
    );
  }

  countOrderItems(completionStatus: boolean, orderStatus: boolean, orderableStatus: boolean): Observable<number> {
    let url = this.toolUrl + '/orderitems/count';
    url += this.fillOrderItemFilter(null, completionStatus, orderStatus, orderableStatus);

    return this.http.get<any>(url).pipe(
      map((response) => {
        const data: number = response.data;
        return data;
      }),
      catchError(this.handleError),
    );
  }

  fillOrderItemFilter(
    orderItemIds: Array<number>,
    completionStatus: boolean,
    orderStatus: boolean,
    orderableStatus: boolean,
  ) {
    let url = '';

    if (orderItemIds !== null || completionStatus !== null || orderStatus !== null || orderableStatus !== null) {
      let q = '';
      if (completionStatus !== null) {
        q += 'completionStatus=' + completionStatus;
      }
      if (orderStatus !== null) {
        if (q.length > 0) {
          q += '&';
        }
        q += 'orderStatus=' + orderStatus;
      }
      if (orderItemIds !== null) {
        if (q.length > 0) {
          q += '&';
        }
        q += 'orderItemIds=' + orderItemIds.join(',');
      }
      if (orderableStatus !== null) {
        if (q.length > 0) {
          q += '&';
        }
        q += 'orderableStatus=' + orderableStatus;
      }
      url += '?' + q;
    }

    return url;
  }

  getOrderItem(id: number): Observable<ARBleOrderItem> {
    return this.http.get<any>(this.toolUrl + '/orderitems/' + id).pipe(
      map((response) => {
        const orderItem: ARBleOrderItem = response.data;
        this.handleOrderItemDates(orderItem);
        return orderItem;
      }),
      catchError(this.handleError),
    );
  }

  getOrderItemByParcel(parcel: Parcel): Observable<ARBleOrderItem> {
    return this.http.get<any>(this.toolUrl + '/orderitems/parcel/' + parcel.id).pipe(
      map((response) => {
        const orderItem: ARBleOrderItem = response.data;
        this.handleOrderItemDates(orderItem);
        return orderItem;
      }),
      catchError(this.handleError),
    );
  }

  private handleOrderItemDates(orderItem: ARBleOrderItem): ARBleOrderItem {
    if (orderItem.t3LastAzoteAlreadySpreadDate) {
      orderItem.t3LastAzoteAlreadySpreadDate = new Date(<string>orderItem.t3LastAzoteAlreadySpreadDate);
    }

    orderItem.createdAt = new Date(<string>orderItem.createdAt);
    orderItem.updatedAt = new Date(<string>orderItem.updatedAt);

    return orderItem;
  }

  saveOrderItem(orderItem: ARBleOrderItem): Observable<ARBleOrderItem> {
    if (orderItem.id) {
      return this.putOrderItem(orderItem);
    }
    return this.postOrderItem(orderItem);
  }

  deleteOrderItem(orderItem: ARBleOrderItem) {
    const url = `${this.toolUrl}/orderitems/${orderItem.id}`;

    return this.http.delete(url).pipe(catchError(this.handleError));
  }

  deleteParcelOrderItem(parcel: Parcel) {
    const url = `${this.toolUrl}/orderitems/parcel/${parcel.id}`;

    return this.http.delete(url).pipe(catchError(this.handleError));
  }

  deleteParcelOrderItems(parcelIds: number[]): Observable<OrderItemDeletionSummary> {
    const url = `${this.toolUrl}/orderitems/parcel/delete-batch`;
    const body = {
      parcelIds: parcelIds,
    };

    return this.http.post<any>(url, body).pipe(
      map((response) => {
        const parsed = tryJson(response);
        if (parsed && parsed.data) {
          return normalizeDeletionSummary(parsed.data);
        }
        return normalizeDeletionSummary(parsed);
      }),
      catchError(this.handleError),
    );
  }

  exportKml(t1: boolean, t2: boolean, t3: boolean): Observable<Blob> {
    // FIXME plus utilisé
    let url = this.toolUrl + '/kml';
    if (t1) {
      url += '?onlyT1=true';
    } else if (t2) {
      url += '?onlyT2=true';
    } else {
      url += '?onlyT3=true';
    }
    const applicationType = 'application/zip';
    return this.httpWanaka.blobQuery(url, applicationType);
  }

  // Add new orderItem
  private postOrderItem(config: ARBleOrderItem): Observable<ARBleOrderItem> {
    return this.http.post<any>(this.toolUrl + '/orderitems', config).pipe(
      map((res) => res.data),
      catchError(this.handleError),
    );
  }

  private putOrderItem(orderItem: ARBleOrderItem): Observable<ARBleOrderItem> {
    const url = `${this.toolUrl}/orderitems/${orderItem.id}`;
    return this.http.put<any>(url, orderItem).pipe(
      map((res) => res.data),
      catchError(this.handleError),
    );
  }

  getPageOrders(
    pageIndex: number,
    itemsPerPage: number,
    sortColumnName: string,
    sortColumnOrder: string,
    filter: AROrderFilter,
  ): Observable<Page<ARBleOrder>> {
    let url = this.toolUrl + '/orders?size=' + itemsPerPage + '&page=' + (pageIndex - 1);
    url += fillOrderFilter(filter);
    if (sortColumnName) {
      url += '&sort=' + sortColumnName + ',' + sortColumnOrder;
    }
    return this.http.get<any>(url).pipe(
      map((response) => {
        const data: Page<ARBleOrder> = response.data;
        for (const order of data.content) {
          this.handleOrderDates(order);
          if (order.campaign) {
            this.handleCampaignDates(order.campaign);
            if (order.campaign.configuration) {
              this.handleConfigurationDates(order.campaign.configuration);
            }
          }
        }
        return data;
      }),
      catchError(this.handleError),
    );
  }

  getOrder(id: number): Observable<ARBleOrder> {
    return this.http.get<any>(this.toolUrl + '/orders/' + id).pipe(
      map((response) => {
        const data: ARBleOrder = response.data;
        this.handleOrderDates(data);
        if (data.campaign) {
          this.handleCampaignDates(data.campaign);
          if (data.campaign.configuration) {
            this.handleConfigurationDates(data.campaign.configuration);
          }
        }
        return data;
      }),
      catchError(this.handleError),
    );
  }

  saveOrder(order: ARBleOrder): Observable<ARBleOrder> {
    if (order.id) {
      return this.putOrder(order);
    }
    return this.postOrder(order);
  }

  deleteOrder(order: ARBleOrder) {
    const url = `${this.toolUrl}/orders/${order.id}`;

    return this.http.delete(url).pipe(catchError(this.handleError));
  }

  getAreaForOrders(filter: AROrderFilter, specy: string): Observable<OrderSummary> {
    let url = `${this.toolUrl}/orders/area?`;
    url += fillOrderFilter(filter);
    if (specy != null) {
      url += `&specy=${specy}`;
    }
    return this.http.get<any>(url).pipe(
      map((res) => res.data),
      catchError(this.handleError),
    );
  }

  notifyUsersCampaign(filter: ARCampaignNotificationFilter, messageType: string): Observable<User> {
    const url = `${this.toolUrl}/campaigns/notification`;
    const dto = { filter: filter, notificationType: messageType };
    return this.http.post<any>(url, dto).pipe(
      map((res) => res.data),
      catchError(this.handleError),
    );
  }

  canPerfomLifecycleUpdates(): Observable<boolean> {
    const url = `${this.toolUrl}/results/canManage`;
    return this.http.get<any>(url).pipe(
      map((res) => res.data),
      catchError(this.handleError),
    );
  }

  // Add new orderItem
  private postOrder(order: ARBleOrder): Observable<ARBleOrder> {
    return this.http.post<any>(this.toolUrl + '/orders', order).pipe(
      map((res) => res.data),
      catchError(this.handleError),
    );
  }

  private putOrder(order: ARBleOrder): Observable<ARBleOrder> {
    const url = `${this.toolUrl}/orders/${order.id}`;

    return this.http.put<any>(url, order).pipe(
      map((res) => res.data),
      catchError(this.handleError),
    );
  }
}
